import { findBackoffProblems } from '@ns/resilience';
import type {
  BreakerConfig,
  BulkheadConfig,
  CoalesceOptions,
  Policy,
  RetryConfig,
  ThrottleConfig,
} from './types';

/**
 * The numbers this judges, named on their own rather than as an options
 * variant, so that a raw call and a decoded one both land here unchanged.
 */
export interface Limits {
  coalesce?: CoalesceOptions;
  retry?: RetryConfig | Policy;
  timeout?: number;
}

/**
 * The largest delay a timer can be handed.
 *
 * Node's timer takes a 32-bit signed delay. Above this it CLAMPS to 1 ms and
 * warns, so a caller asking for a very long deadline would silently get the
 * shortest one there is — the opposite of what they wrote. Refusing the value
 * is what keeps the wish and the behavior the same thing.
 */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * A span of time that is more than nothing and still fits the timer.
 *
 * TWO BOUNDS AND NOTHING ELSE, `NaN` and the infinities included. `NaN` compares
 * false against everything, so it fails the first bound (TO9); `Infinity` is
 * above the ceiling (TO8) and `-Infinity` is not in the future. A
 * `Number.isFinite` in front of them said the same thing a third time and is
 * gone — MEASURED, removing it leaves every row of `unusable-values.test.ts`
 * green, and those rows are where the three values are actually held.
 *
 * ONE RULE FOR `timeout` AND FOR THE THROTTLE'S WINDOW, because both numbers
 * turn into their own opposite at the same two edges: a `timeout` of nothing
 * grants no time where it reads as "no limit" (TO7), and a window of no length
 * drops every moment the instant it is taken, so the throttle lets everything
 * through while one is configured (TH13).
 *
 * Exported because the clock starts before `checkLimits` runs and must not be
 * armed on a number this refuses — see `startDeadline`. One predicate read from
 * two places, rather than two answers that can drift apart.
 */
export function usableDuration(ms: number): boolean {
  return ms > 0 && ms <= MAX_TIMER_MS;
}

/**
 * A whole number of something, at least one of it.
 *
 * `Number.isInteger` is what rules out `NaN`, `Infinity` and a fraction in one
 * call, and all three belong here for the same reason `timeout: Infinity` does
 * (RC25): "at most infinitely many" is a way of writing "no limit", and nobody
 * counts down 2.5 attempts. What is left to say is that a call has to happen.
 *
 * `retry.maxAttempts`, `throttle.limit` and `bulkhead.maxConcurrent` are the
 * same sentence about three different things, down to what nought does to them:
 * it reads as a limit and works as a wall. Nought attempts never calls, nought
 * per window queues forever (TH12), nought at a time runs nothing.
 */
function usableCount(count: number): boolean {
  return Number.isInteger(count) && count >= 1;
}

/**
 * A whole number of places to wait in, none of them being one of the answers.
 *
 * ZERO IS USABLE HERE AND REFUSED ON `usableCount`, and the difference is again
 * what the number means. `maxConcurrent: 0` says no call may ever run, which is
 * the wall described there; `queueSize: 0` says nobody waits, which `bulkhead`
 * already keeps as a real configuration -- shed the load rather than accumulate
 * it (BH5). Harmonising the two would take away one of those meanings.
 *
 * `Number.isInteger` carries `Infinity` out for a reason of its own: a queue
 * without a bottom does not prevent the collapse the bulkhead exists for, it
 * schedules it for later with more work already in flight.
 */
function usableQueueSize(size: number): boolean {
  return Number.isInteger(size) && size >= 0;
}

/**
 * A wait the timer can perform, and can perform for as long as it was asked to.
 *
 * Exported for `worthWaiting`, which needs a floor of its own: the deadline it
 * used to lean on answers `Infinity` while the clock is unarmed, and everything
 * fits under that. One predicate read from two places, rather than two answers
 * that can drift apart -- the same reason `usableDuration` is exported.
 *
 * ZERO IS USABLE HERE AND REFUSED ON `timeout`, and the difference is what the
 * number means rather than one rule being softer than the other. A `timeout` of
 * zero grants no time at all, which is the opposite of the "no limit" it reads
 * as (TO7); a delay of zero says "try again at once", which is a wish someone
 * can hold and write down exactly (RC42). Harmonising the two would take away
 * one of those meanings.
 *
 * A fraction is left alone for the same reason it is not on `usableCount`:
 * half a millisecond is a timer that fires, where half an attempt is nothing
 * anyone could count down.
 *
 * The ceiling is `MAX_TIMER_MS` for the reason given there -- above it the long
 * backoff becomes the immediate retry the backoff exists to prevent.
 *
 * The bounds carry `NaN` and the infinities on their own, exactly as they do on
 * `usableDuration`, so there is no finiteness check in front of them here either.
 *
 * `@ns/resilience` keeps a private copy of this predicate in `backoff.ts`, where
 * `findBackoffProblems` judges every `backoff` number with it. The two should
 * stay equal.
 */
export function usableDelay(ms: number): boolean {
  return ms >= 0 && ms <= MAX_TIMER_MS;
}

/**
 * What is wrong with the `timeout`, or nothing when it is usable.
 *
 * The wording is `usableDuration`'s, and one predicate carries ONE wording
 * (TH22): the reader has to be able to tell from the sentence whether nought is
 * included, and here it is not.
 */
function problemsWithTimeout(timeout: number | undefined): readonly string[] {
  if (timeout === undefined || usableDuration(timeout)) return [];
  return [
    `timeout must be greater than 0 and at most ${MAX_TIMER_MS} ms, not ${timeout}`,
  ];
}

/**
 * Everything wrong with the retry config, and not just the first of it.
 *
 * ALL THREE FIELDS ARE READ. Stopping at the first would hand a caller who got
 * two numbers wrong a second refusal only after they fixed the first, and one
 * of the two would still have been in the message the whole time.
 *
 * The delays are judged on the VALUE, not on a path that could carry them to a
 * timer. The path that was found needed three mistakes at once -- an unusable
 * `timeout`, which leaves the deadline unarmed so nothing bounds the wait, plus
 * a `shouldRetry` that says yes to everything, plus the oversized delay -- and a
 * check shaped like that path would watch that one combination while missing
 * every other way the same number arrives (RC43).
 */
function problemsWithRetry(retry: RetryConfig | Policy | undefined): string[] {
  // What the value IS decides, not which properties it happens to carry: a
  // `Policy` takes the retry position over entirely, so it has no attempt count
  // to check and passes through untouched.
  if (retry === undefined || typeof retry === 'function') return [];

  const problems: string[] = [];
  const { backoff, maxAttempts, perAttemptTimeoutMs } = retry;

  if (!usableCount(maxAttempts)) {
    problems.push(
      `retry.maxAttempts must be a whole number of at least 1, not ${maxAttempts}`,
    );
  }

  problems.push(...findBackoffProblems(backoff, maxAttempts, 'retry.backoff'));

  // `usableDuration` and NOT `usableDelay`, which is the one point the two
  // predicates differ on and the reason each field has to pick deliberately: an
  // attempt allowed no time at all cannot do anything, so nought here is a
  // mistake rather than a setting (RC66). Leaving the field out is what says
  // "no bound of its own", and a second way to say that is the precedence
  // question this shape exists to avoid.
  if (
    perAttemptTimeoutMs !== undefined &&
    !usableDuration(perAttemptTimeoutMs)
  ) {
    problems.push(
      `retry.perAttemptTimeoutMs must be greater than 0 and at most ${MAX_TIMER_MS} ms, not ${perAttemptTimeoutMs}`,
    );
  }

  return problems;
}

/**
 * What is wrong with `coalesce.cache`, judged for a JavaScript caller: the
 * compiler already refuses every one of these shapes, which is why the values
 * are read as `unknown`.
 *
 * `clone: false` is refused because a kept answer is always copied, in and out,
 * so "share the object" cannot be true there. A store of the caller's own is
 * judged on the three methods coalescing calls on it, and a `WeakMap` is
 * refused because it takes no string key. Its numbers are judged by `CacheMap`
 * itself, at its construction.
 */
function problemsWithCoalesceCache(
  coalesce: CoalesceOptions | undefined,
): string[] {
  if (typeof coalesce !== 'object' || coalesce.cache === undefined) return [];
  const cache: unknown = coalesce.cache;
  const clone: unknown = coalesce.clone;
  const problems =
    clone === false
      ? [
          'coalesce.clone must be true or left out when coalesce.cache is set, not false',
        ]
      : [];
  if (typeof cache !== 'object' || cache === null) {
    return [
      ...problems,
      `coalesce.cache must be a CacheMap or { ttl, maxEntries? }, not ${shownValue(
        cache,
      )}`,
    ];
  }
  return [
    ...problems,
    ...(meansAStore(cache)
      ? problemsWithOwnStore(cache)
      : problemsWithCacheSettings(cache)),
  ];
}

/**
 * A refused value as a message can show it: a function or an object by its
 * kind, anything else as `String` writes it. A function's source can carry a
 * secret, and an object would read as `[object Object]`.
 */
function shownValue(value: unknown): string {
  if (typeof value === 'function') return 'a function';
  if (typeof value === 'object' && value !== null) return 'an object';
  return String(value);
}

/** Anything with one of the three methods coalescing calls means to be a store. */
function meansAStore(cache: object): boolean {
  return 'set' in cache || 'get' in cache || 'has' in cache;
}

/**
 * A store of the caller's own: `has` and `get` on a lookup, `set` on a keep,
 * all under a string key, which a `WeakMap` refuses.
 */
function problemsWithOwnStore(cache: object): string[] {
  if (Object.prototype.toString.call(cache) === '[object WeakMap]') {
    return [
      'coalesce.cache must not be a WeakMap: a kept answer is stored under a string key',
    ];
  }
  const usable = ['has', 'get', 'set'].every(
    name => typeof Reflect.get(cache, name) === 'function',
  );
  return usable
    ? []
    : [
        'coalesce.cache must have has, get and set as functions to be a store of your own',
      ];
}

/** The inline settings: a finite `ttl` of 0 or more, and a whole `maxEntries`. */
function problemsWithCacheSettings(cache: object): string[] {
  const problems: string[] = [];
  const ttl: unknown = Reflect.get(cache, 'ttl');
  const maxEntries: unknown = Reflect.get(cache, 'maxEntries');
  if (typeof ttl !== 'number' || !Number.isFinite(ttl) || ttl < 0) {
    problems.push(
      `coalesce.cache.ttl must be a finite number of 0 or more, not ${shownValue(
        ttl,
      )}`,
    );
  }
  if (
    maxEntries !== undefined &&
    !(typeof maxEntries === 'number' && usableCount(maxEntries))
  ) {
    problems.push(
      `coalesce.cache.maxEntries must be a whole number of at least 1, not ${shownValue(
        maxEntries,
      )}`,
    );
  }
  return problems;
}

/**
 * Refuses a number the library cannot act on, so that it is refused at the
 * caller rather than as a puzzling failure minutes later.
 *
 * THE ONE MOMENT THE OPTIONS ARE JUDGED (UR21). The slice validators above stay
 * where they live and keep their own subject; what became a single place is
 * WHEN they are asked. Until then this ran inside the closure the retry loop
 * calls, so a bug in the CALL was handed to the loop as though it were a bug in
 * the upstream: with `timeout: 0` leaving the deadline unarmed and
 * `maxAttempts: Infinity` leaving the budget open, the call repeated its own
 * refusal without ever reaching a transport (TO36).
 *
 * EVERY PROBLEM, ONE REFUSAL, one per line. Two unusable numbers are one bug in
 * one call, and reporting them one release at a time is the drip TO36 was found
 * by.
 *
 * Throws the platform `RangeError` and the caller relabels it, the way
 * `resolveAddress` and `toHeaders` are relabelled. This function stays free of
 * the error vocabulary so it can be read as "which numbers are usable".
 */
export function validateOptions({ coalesce, retry, timeout }: Limits): void {
  const problems = [
    ...problemsWithTimeout(timeout),
    ...problemsWithRetry(retry),
    ...problemsWithCoalesceCache(coalesce),
  ];
  if (problems.length === 0) return;
  throw new RangeError(problems.join('\n'));
}

/**
 * Refuses a quota or a window the throttle cannot act on.
 *
 * Read at `throttle(...)` and not at the first call, because the alternative is
 * not a failure at all: an unusable number here leaves a caller waiting on a
 * wake-up that cannot come, until whatever deadline it happens to carry ends it
 * (TH12, TH13). A construction the caller is standing in front of is the one
 * moment where the number and the person who wrote it are still together.
 */
export function checkThrottleConfig({
  intervalMs,
  limit,
}: ThrottleConfig): void {
  if (!usableCount(limit)) {
    throw new RangeError(
      `throttle.limit must be a whole number of at least 1, not ${limit}`,
    );
  }

  if (!usableDuration(intervalMs)) {
    throw new RangeError(
      `throttle.intervalMs must be greater than 0 and at most ${MAX_TIMER_MS} ms, not ${intervalMs}`,
    );
  }
}

/**
 * Refuses a threshold or a cooldown the breaker cannot act on, at `breaker(...)`
 * for the reason given on `checkThrottleConfig`.
 *
 * This one was missing while its two siblings had it, and both fields are read
 * in a bare `>=`. A comparison against `NaN` is false either way round, which
 * gives two opposite silent failures: a `NaN` threshold never opens the circuit,
 * so the policy protects nothing while reading as protection (BR28), and a `NaN`
 * cooldown never closes it again, so every later call is refused until the
 * process restarts (BR29).
 *
 * `halfOpenAfterMs: 0` stays valid. Nought here is not the throttle's empty
 * window but the bulkhead's absent queue: it means the next arrival is the
 * probe, which is a thing a caller can want.
 *
 * NO `MAX_TIMER_MS` CEILING ON THE COOLDOWN, and that is the one place this
 * check departs from its two siblings. `usableDelay` carries that ceiling
 * because above it a `setTimeout` wraps and a long backoff becomes the
 * immediate retry the backoff exists to prevent. The breaker arms no timer: it
 * asks whether `Date.now() - openedAt` has reached the cooldown, and a
 * comparison does not wrap. Borrowing the ceiling refused a caller of this
 * package a config that `@ns/resilience` accepted for the same guard, which is
 * two answers to one question. BR30 is the owner of that.
 *
 * What it still refuses is what the underlying guard refuses: `NaN` and the
 * infinities, both of which leave the circuit stuck in the bare `>=` below.
 */
export function checkBreakerConfig({
  failureThreshold,
  halfOpenAfterMs,
}: BreakerConfig): void {
  if (!usableCount(failureThreshold)) {
    throw new RangeError(
      `breaker.failureThreshold must be a whole number of at least 1, not ${failureThreshold}`,
    );
  }

  if (!Number.isFinite(halfOpenAfterMs) || halfOpenAfterMs < 0) {
    throw new RangeError(
      `breaker.halfOpenAfterMs must be a finite number of at least 0, not ${halfOpenAfterMs}`,
    );
  }
}

/**
 * Refuses a pool or a queue the bulkhead cannot act on, at `bulkhead(...)` for
 * the reason given on `checkThrottleConfig`.
 */
export function checkBulkheadConfig({
  maxConcurrent,
  queueSize,
}: BulkheadConfig): void {
  if (!usableCount(maxConcurrent)) {
    throw new RangeError(
      `bulkhead.maxConcurrent must be a whole number of at least 1, not ${maxConcurrent}`,
    );
  }

  if (!usableQueueSize(queueSize)) {
    throw new RangeError(
      `bulkhead.queueSize must be a whole number of at least 0, not ${queueSize}`,
    );
  }
}
